import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";

import ServiceFormModal from "../../components/AdminSideComponent/Services/Serviceformmodal";
import DeleteConfirmModal from "../../components/AdminSideComponent/Common/Deleteconfirmmodal";

import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/services`;

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalType, setModalType] = useState(null);

  const [selectedService, setSelectedService] = useState(null);

  const [expandedService, setExpandedService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await axios.get(`${API_BASE_URL}/getAll`);

      setServices(data.data.data || data);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = () => {
    setSelectedService(null);
    setModalType("add");
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setModalType("edit");
  };

  const handleDeleteService = (service) => {
    setSelectedService(service);
    setModalType("delete");
  };

  const handleCloseModal = () => {
    setModalType(null);
    setSelectedService(null);
  };

  const handleServiceSaved = () => {
    fetchServices();
    handleCloseModal();
  };

  const handleServiceDeleted = () => {
    fetchServices();
    handleCloseModal();
    toast.success("Service Deleted Successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Services</h1>
            <p className="text-gray-600 mt-2">Manage your services</p>
          </div>

          <button
            onClick={handleAddService}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            <Plus size={20} />
            Add Service
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 text-lg">No services found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {services.map((service) => (
              <div
                key={service._id}
                className="bg-white rounded-lg shadow hover:shadow-md transition border border-gray-200"
              >
                {/* Card Header */}
                <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                  <div className="flex items-start gap-2 flex-1">
                    {service.icon && (
                      <div className="text-xl flex-shrink-0">
                        {service.icon}
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {service.title}
                      </h3>

                      <p className="text-gray-600 mt-2">{service.tagline}</p>

                      <p className="text-sm text-gray-500 mt-3">
                         <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold mr-2 ${
                          service.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {service.isActive ? "Active" : "Inactive"}
                      </span>
                        <span className="mr-2">Slug:</span>
                        <code className="bg-gray-100 px-2 py-1 rounded">
                          {service.slug}
                        </code>
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditService(service)}
                      className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
                    >
                      <Edit2 size={20} />
                    </button>

                    <button
                      onClick={() => handleDeleteService(service)}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                    >
                      <Trash2 size={20} />
                    </button>

                    <button
                      onClick={() =>
                        setExpandedService(
                          expandedService === service._id ? null : service._id,
                        )
                      }
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg"
                    >
                      {expandedService === service._id ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expandable Details */}
                {expandedService === service._id && (
                  <div className="p-6 space-y-6 text-sm bg-gradient-to-br from-gray-50 via-white to-gray-50 border-t border-gray-200">
                    {service.shortDesc && (
                      <div className="relative overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-md transition p-5">
                        <div className="absolute top-0 left-0 h-full w-1 bg-blue-500"></div>

                        <h4 className="text-blue-600 font-semibold text-sm uppercase tracking-wide">
                          Description
                        </h4>

                        <p className="text-gray-700 mt-2 leading-relaxed">
                          {service.shortDesc}
                        </p>
                      </div>
                    )}

                    {service.stats?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-purple-600 font-semibold text-sm uppercase mb-4">
                          Key Metrics
                        </h4>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {service.stats.map((s, i) => (
                            <div
                              key={i}
                              className="bg-gradient-to-br from-purple-50 to-white border rounded-lg p-3 hover:scale-[1.02] transition"
                            >
                              <p className="text-xl font-bold text-purple-700">
                                {s.value}
                              </p>
                              <p className="text-gray-600 text-xs">{s.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.overview && (
                      <div className="rounded-xl border bg-violet-50 shadow-sm p-5">
                        <h4 className="text-yellow-600 font-semibold text-sm uppercase mb-3">
                          Overview
                        </h4>

                        <p className="text-gray-900 font-medium">
                          {service.overview.heading}
                        </p>

                        <p className="text-gray-600 mt-2  leading-relaxed">
                          {service.overview.body}
                        </p>
                      </div>
                    )}

                    {service.features?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-indigo-600 font-semibold text-sm uppercase mb-4">
                          Features
                        </h4>

                        <div className="grid md:grid-cols-2 gap-3">
                          {service.features.map((f, index) => (
                            <div
                              key={index}
                              className="p-4 rounded-lg border bg-gradient-to-br from-indigo-50 to-white hover:shadow-md transition"
                            >
                              <p className="font-semibold text-gray-900">
                                {f.title}
                              </p>
                              <p className="text-gray-600 text-sm mt-1">
                                {f.desc}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.process?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-emerald-600 font-semibold text-sm uppercase mb-4">
                          Process
                        </h4>

                        <div className="space-y-3">
                          {service.process.map((p, i) => (
                            <div
                              key={i}
                              className="flex gap-4 p-3 rounded-lg bg-emerald-50 border hover:shadow-sm transition"
                            >
                              <div className="font-bold text-emerald-700 min-w-[60px]">
                                {p.step}
                              </div>

                              <div>
                                <p className="font-semibold text-gray-900">
                                  {p.title}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  {p.desc}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.benefits?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-cyan-600 font-semibold text-sm uppercase mb-4">
                          Benefits
                        </h4>

                        <div className="flex flex-wrap gap-2">
                          {service.benefits.map((b, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 rounded-full bg-cyan-50 border text-cyan-700 text-sm hover:bg-cyan-100 transition"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.industries?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-blue-600 font-semibold text-sm uppercase mb-4">
                          Industries
                        </h4>

                        <div className="flex flex-wrap gap-2">
                          {service.industries.map((i, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full bg-blue-50 border text-blue-700 text-sm hover:bg-blue-100 transition"
                            >
                              {i}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.technologies?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-slate-700 font-semibold text-sm uppercase mb-4">
                          Technologies
                        </h4>

                        <div className="flex flex-wrap gap-2">
                          {service.technologies.map((t, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full bg-slate-100 border text-slate-700 text-sm hover:bg-slate-200 transition"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {service.faq?.length > 0 && (
                      <div className="rounded-xl border bg-white shadow-sm p-5">
                        <h4 className="text-rose-600 font-semibold text-sm uppercase mb-4">
                          FAQ
                        </h4>

                        <div className="space-y-3">
                          {service.faq.map((f, i) => (
                            <div
                              key={i}
                              className="p-3 rounded-lg border bg-rose-50 hover:shadow-sm transition"
                            >
                              <p className="font-semibold text-gray-900">
                                Q: {f.q}
                              </p>
                              <p className="text-gray-600 text-sm mt-1">
                                A: {f.a}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(modalType === "add" || modalType === "edit") && (
        <ServiceFormModal
          mode={modalType}
          service={selectedService}
          onClose={handleCloseModal}
          onSave={handleServiceSaved}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {modalType === "delete" && selectedService && (
        <DeleteConfirmModal
          product={selectedService}
          onClose={handleCloseModal}
          onDelete={handleServiceDeleted}
          apiBaseUrl={API_BASE_URL}
        />
      )}
    </div>
  );
};

export default ServiceList;
